import classNames from 'classnames';

import React, {
  FC,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
} from 'react';

import { Icon as IconComponent } from '../icon';
import { Image as ImageComponent } from '../image';
import { Typography } from '../typography';

import styles from './chip.scss';

type Props = RefAttributes<HTMLDivElement> &
  Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> & {
    value: string | number;
    checked?: boolean;
    className?: string;
    onChange?: (value: string | number, checked: boolean) => void;
    icon?: string | ReactElement;
    image?: string | ReactElement;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-chip';

const Icon: FC<Pick<Props, 'icon' | 'dataTestId'>> = ({ icon, dataTestId }) => {
  if (icon) {
    return typeof icon === 'string' ? <IconComponent name={icon} dataTestId={dataTestId} /> : icon;
  }
  return null;
};

const Image: FC<Pick<Props, 'image' | 'value' | 'dataTestId'>> = ({ image, value, dataTestId }) => {
  if (image) {
    return typeof image === 'string' ? (
      <ImageComponent
        name={image}
        width="24"
        height="24"
        alt={String(value)}
        dataTestId={dataTestId}
      />
    ) : (
      image
    );
  }
  return null;
};

export const Chip: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      checked,
      className,
      value,
      onChange,
      icon,
      image,
      children,
      dataTestId = COMPONENT_ID,
      ...rest
    },
    ref,
  ) => {
    const classes = classNames(styles.container, className, {
      [styles.active]: checked,
      [styles.labelHasIcon]: icon || image,
    });

    const handleChange = () => onChange?.(value, checked);

    return (
      <div
        data-component-name={COMPONENT_ID}
        data-testid={dataTestId}
        data-id={dataTestId}
        className={classes}
        ref={ref}
        {...rest}>
        <label>
          <input
            type="checkbox"
            name="checkbox"
            value={value}
            checked={checked}
            onChange={handleChange}
            data-testid={`${dataTestId}-input`}
          />
          <Icon icon={icon} dataTestId={`${dataTestId}-icon`} />
          <Image image={image} value={value} dataTestId={`${dataTestId}-image`} />
          <Typography
            tag="p"
            dataTestId={`${dataTestId}-text`}
            variant={checked ? 'subhead-semibold' : 'subhead-regular'}>
            {children}
          </Typography>
        </label>
      </div>
    );
  },
);

Chip.displayName = 'Chip';
